/**
 * Folha "Marcar ponto de encontro": onde (minha posição, ou um lugar pela
 * busca), quando (hora), um nome curto. Vai por push a todos.
 */
import { uuidv7 } from '@nv/core';
import { useState } from 'react';

import { useTrip } from '../../trip/TripContext';
import { useCompartilharPosicao } from '../mapa/compartilhar';
import { LocalField, type LugarDaAtividade } from '../roteiro/LocalField';
import { encontroApi, useInvalidarEncontro } from './api';
import { Sheet } from '../../ui/Sheet';

function horaDaquiA(min: number): string {
  const d = new Date(Date.now() + min * 60_000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function MarcarEncontro({ onFechar }: { onFechar: () => void }) {
  const { tripId } = useTrip();
  const minha = useCompartilharPosicao((s) => s.ultima);
  const avisar = useInvalidarEncontro(tripId);
  const [titulo, setTitulo] = useState('');
  const [lugar, setLugar] = useState<LugarDaAtividade | null>(null);
  // O texto digitado em "Escolher um lugar" (#79, Onda 1): o `LocalField` é
  // controlado, então o que a pessoa digita precisa viver em estado — antes,
  // só o lugar estruturado era guardado e cada letra era descartada.
  const [buscaLugar, setBuscaLugar] = useState('');
  const [usarMinha, setUsarMinha] = useState(!!minha);
  const [hora, setHora] = useState(horaDaquiA(30));
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const coords = usarMinha && minha ? { latitude: minha.latitude, longitude: minha.longitude } : lugar ? { latitude: lugar.latitude, longitude: lugar.longitude } : null;
  const pode = titulo.trim().length > 0 && !!coords && /^([01]\d|2[0-3]):[0-5]\d$/.test(hora);

  async function marcar() {
    if (!pode || !coords) return;
    setEnviando(true);
    setErro(null);
    try {
      const [hh, mm] = hora.split(':').map(Number) as [number, number];
      const at = new Date();
      at.setHours(hh, mm, 0, 0);
      // Hora já passada hoje = amanhã, não "há 20 horas".
      if (at.getTime() < Date.now() - 60_000) at.setDate(at.getDate() + 1);
      await encontroApi.criar(tripId, { id: uuidv7(), title: titulo.trim(), latitude: coords.latitude, longitude: coords.longitude, at: at.toISOString() });
      avisar();
      onFechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível marcar agora.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Sheet titleId="enc-marcar" onFechar={onFechar}>
        <h2 className="sheet__title" id="enc-marcar">Ponto de encontro</h2>
        <label className="field">
          <span className="field__label">Onde (nome curto)</span>
          <input className="field__input" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Portão 3, em frente ao carrossel…" maxLength={80} aria-label="Onde" />
        </label>
        <div className="chip-row" role="radiogroup" aria-label="Lugar">
          <button type="button" role="radio" aria-checked={usarMinha} className={usarMinha ? 'pick pick--on' : 'pick'} disabled={!minha} onClick={() => setUsarMinha(true)}>
            Minha posição{!minha ? ' (ligue o mapa)' : ''}
          </button>
          <button type="button" role="radio" aria-checked={!usarMinha} className={!usarMinha ? 'pick pick--on' : 'pick'} onClick={() => setUsarMinha(false)}>
            Escolher um lugar
          </button>
        </div>
        {!usarMinha && (
          <LocalField rotulo="Lugar" nome={buscaLugar} lugar={lugar} onChange={(nome, l) => { setBuscaLugar(nome); setLugar(l); if (l && !titulo) setTitulo(nome); }} />
        )}
        <label className="field">
          <span className="field__label">Hora</span>
          <input className="field__input" type="time" value={hora} onChange={(e) => setHora(e.target.value)} aria-label="Hora" />
        </label>
        <div className="chip-row">
          {[15, 30, 60].map((m) => (
            <button type="button" key={m} className="pick" onClick={() => setHora(horaDaquiA(m))}>
              em {m} min
            </button>
          ))}
        </div>
        {erro && <p className="auth-error" role="alert">{erro}</p>}
        <div className="fin__ctas">
          <button type="button" className="nv-btn nv-btn--secondary" onClick={onFechar}>Cancelar</button>
          <button type="button" className="nv-btn nv-btn--primary" disabled={!pode || enviando} onClick={() => void marcar()}>
            {enviando ? 'Marcando…' : 'Marcar e avisar todos'}
          </button>
        </div>
    </Sheet>
  );
}
